import { formatCurrency, formatNumber, formatPercentage, isNumber, parseNumber, selectAllOnFocus, SetState, updateObj } from "./Common";
import { Autocomplete, Button, Checkbox, IconButton, Input, InputAdornment, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { Quote, QuoteFormSettings } from "./Quotes";
import { useContext, useEffect, useState } from "react";
import { Product } from "./Products";
import { Supplier } from "./Suppliers";
import { ItemDelivery, ItemDeliveryDate } from "./QuotesDelivery";
import { RequestItem } from "./Requests";
import { UserContext, userIsEmployeeOrAdmin, UserRoleClient, UserRoleEmployee, UserRoleUnknown } from "./Users";

export interface Item {
    id: number;
	quote_no: string;
	description: string;
	item_size: string;
	units: number;
	price: number;
    priceText: string;
	cost: number;
    costText: string;
	vat: number;
	supplier: string;
}

export interface FinancialSummary {
    subtotalExcVAT: number;
    subtotalVAT: number;
    serviceExcVAT: number;
    serviceVAT: number;
    serviceFee: number;
    vat_rate: number;
    costTotalExcVAT: number;
    costVAT: number;
    roundValues: number;
}

export const DefaultFinancialSummary: FinancialSummary = {
    subtotalExcVAT: 0,
    subtotalVAT: 0,
    serviceExcVAT: 0,
    serviceVAT: 0,
    serviceFee: 0,
    vat_rate: 0,
    costTotalExcVAT: 0,
    costVAT: 0,
    roundValues: 0,
}

function defaultItem(id: number, quoteNo: string, settings: QuoteFormSettings): Item {
    return {
        id: id,
        quote_no: quoteNo,
        description: '',
        item_size: '',
        units: 0,
        price: 0,
        priceText: "0",
        cost: 0,
        costText: "0",
        vat: 1,
        supplier: settings.default_supplier,
    }
}

export function mapDbItemsToLocal(item: Item, index: number): Item {
    return {
        ...item,
        id: index,
        costText: formatNumber(item.cost),
        priceText: formatNumber(item.price),
    };
}

export function mapDbRequestItemsToQuoteItems(item: RequestItem, index: number, quoteNo: string): Item {
    return {
        id: index,
        quote_no: quoteNo,
        description: item.description,
        item_size: item.item_size,
        units: item.units,
        price: item.price,
        priceText: formatNumber(item.price),
        cost: 0,
        costText: formatNumber(0),
        vat: item.vat,
        supplier: item.supplier,
    }
}

export function findItemSuggestions(text: string, products: Array<Product>, suppliers: Array<Supplier>, settings: QuoteFormSettings): Array<Product | any> {
    const result = products
        .filter((product) => product.description.includes(text) || product.item_size.includes(text))
        .map((product, index) => { 
            const supplier = suppliers.find((supplier) => supplier.id === product.supplier_id)?.name || ""; 
            return {
                ...product, 
                supplier_text: supplier,
                label: (
                    supplier === "" || supplier === settings.default_supplier || !settings.show_supplier_column 
                        ? "" 
                        : supplier + " ⇒ "
                    ) + product.description + " 〈" + product.item_size + "〉", 
                id: index
            }
        });
    return result;
} 

export async function searchItemSuggestions(text: string, suppliers: Array<Supplier>, settings: QuoteFormSettings): Promise<Array<Product | any>> {
    if (text.trim().length <= 0) return [];

    const response = await fetch("/api/v1/products?description=" + encodeURIComponent(text));
    const data = await response.json();
    const products: Array<Product> = data.result || [];

    const result = products
        // .filter((product) => product.description.includes(text) || product.item_size.includes(text))
        .map((product, index) => { 
            const supplier = suppliers.find((supplier) => supplier.id === product.supplier_id)?.name || ""; 
            return {
                ...product, 
                supplier_text: supplier,
                label: (
                    supplier === "" || supplier === settings.default_supplier || !settings.show_supplier_column 
                        ? "" 
                        : supplier + " ⇒ "
                    ) + product.description + " 〈" + product.item_size + "〉", 
                id: index
            }
        });
    return result;
} 

export function parseSuggestionDescription(text: string): string {
    var result = text;
    if (text.includes(" ⇒ ")) {
        const comps = text.split(" ⇒ ");
        if (comps.length === 1) {
            return comps[0];
        } else {
            result = comps[1];
        }
    }
    if (text.includes(" 〈")) {
        const comps = result.split(" 〈");
        return comps[0]
    }

    return result;
}

export function ItemRow({ item, setItems, suppliers, settings, quote }: { item: Item, setItems: SetState<Array<Item>>, suppliers: Array<Supplier>, settings: QuoteFormSettings, quote: Partial<Quote> }) {
    const [suggestions, setSuggestions] = useState<Array<Product | any>>([]);

    const [itemDescription, setItemDescription] = useState("");
    useEffect(() => {
        const timeOut = setTimeout(async () => 
            setSuggestions(await searchItemSuggestions(itemDescription, suppliers, settings)), 1000
        );
        return () => clearTimeout(timeOut);
    }, [itemDescription, settings, suppliers]);

    const auth = useContext(UserContext);

    return (
        <TableRow>
            {
                settings.show_supplier_column || (auth.user.role || UserRoleUnknown) === UserRoleClient
                    ? <TableCell>
                        {userIsEmployeeOrAdmin(auth)
                        ? <Input
                            fullWidth
                            onFocus={selectAllOnFocus}
                            value={item.supplier || ""}
                            onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, supplier: ev.target.value})))}/>
                        : (item.supplier || "")
                        }
                      </ TableCell>
                    : <></>
            }
            <TableCell>
                {userIsEmployeeOrAdmin(auth)
                    ? <Autocomplete 
                        fullWidth
                        freeSolo
                        inputValue={item.description || ""}
                        onFocus={selectAllOnFocus}
                        onInputChange={
                            async (_, value) => {
                                const desc = parseSuggestionDescription(value);
                                setItems(items => items.map(it => updateObj(it, {id: item.id, description: desc})));
                                setItemDescription(desc);
                            }
                        }
                        onChange={
                            (_, value) => {
                                if (typeof value === "object") {
                                    setItems(items => items.map(it => (value?.description || "") !== "" ? updateObj(it, {
                                        id: item.id,
                                        description: value?.description || "",
                                        item_size: value?.item_size || "",
                                        cost: value?.cost || 0,
                                        costText: formatNumber(value?.cost),
                                        price: value?.price || 0,
                                        priceText: formatNumber(value?.price),
                                        supplier: value?.supplier_text,
                                        vat: value?.vat || 0
                                    }) : it));
                                }
                            }
                        }
                        renderInput={(params) => <TextField {...params} multiline variant="standard" fullWidth/>}
                        options={suggestions}/>
                    : (item.description || "")
                }
            </TableCell>
            <TableCell>
                {userIsEmployeeOrAdmin(auth)
                    ?  <Input 
                        fullWidth
                        onFocus={selectAllOnFocus}
                        value={item.item_size || ""}
                        onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, item_size: ev.target.value})))}/>
                    : (item.item_size || "")
                }
            </TableCell>
            <TableCell>
                {userIsEmployeeOrAdmin(auth)
                    ? <Input 
                        fullWidth
                        onFocus={selectAllOnFocus}
                        value={item.units || "0"}
                        onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, units: parseInt(ev.target.value) || 0})))}/>
                    : (item.units || "")
                }
            </TableCell>
            <TableCell>
                <Checkbox 
                    checked={item.vat === 1}
                    disabled={(auth.user.role || UserRoleUnknown) > UserRoleEmployee}
                    onChange={(_, checked) => setItems(items => items.map(it => updateObj(it, {id: item.id, vat: checked ? 1 : 0})))}/>
            </TableCell>
            {userIsEmployeeOrAdmin(auth) && <TableCell>
                <Input 
                    fullWidth
                    onFocus={selectAllOnFocus}
                    value={item.costText || ""} 
                    error={!isNumber(item.costText)}
                    onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, cost: parseNumber(ev.target.value), costText: ev.target.value})))}
                    startAdornment={
                        <InputAdornment position="start">R</InputAdornment>
                    }/>
            </TableCell>}
            <TableCell>
                {userIsEmployeeOrAdmin(auth)
                    ? <Input 
                        fullWidth
                        onFocus={selectAllOnFocus}
                        value={item.priceText || ""} 
                        error={!isNumber(item.priceText)}
                        onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, price: parseNumber(ev.target.value), priceText: ev.target.value})))}
                        startAdornment={
                            <InputAdornment position="start">R</InputAdornment>
                        }/>
                    : "R" + (item.priceText || "")
                }
            </TableCell>
            <TableCell>
                {userIsEmployeeOrAdmin(auth)
                    ? <Input 
                        fullWidth
                        onFocus={selectAllOnFocus}
                        contentEditable={false}
                        value={formatNumber(roundValue(quote.round_values || 0, item.price || 0, 10_000) * (item.units || 0))}
                        startAdornment={
                            <InputAdornment position="start">R</InputAdornment>
                        }/>
                    : ("R" + formatNumber(roundValue(quote.round_values || 0, item.price || 0, 10_000) * (item.units || 0)))
                }
            </TableCell>
            {userIsEmployeeOrAdmin(auth) && <TableCell align="center" onClick={ev => setItems(items => items.filter(it => it.id !== item.id))}>
                <IconButton color="error">
                    <DeleteIcon />
                </IconButton>
            </TableCell>}
        </TableRow>
    )
}

export function ItemsTable({ quote, items, setItems, suppliers, settings }: { quote: Partial<Quote>, items: Array<Item>, setItems: SetState<Array<Item>>, suppliers: Array<Supplier>, settings: QuoteFormSettings }) {
    const auth = useContext(UserContext);
    return (
        quote.quote_no === undefined ? <Paper>Select A Client</Paper> :
        <Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        {(settings.show_supplier_column || (auth.user.role || UserRoleUnknown) === UserRoleClient ? <TableCell width="10%">Supplier</TableCell> : <></>)}
                        <TableCell width={settings.show_supplier_column ? "20%" : "30%"}>Description</TableCell>
                        <TableCell width="15%">Item Size</TableCell>
                        <TableCell width="10%">Units</TableCell>
                        <TableCell width="2.5%">VAT</TableCell>
                        {userIsEmployeeOrAdmin(auth) && <TableCell width="15%">Cost</TableCell>}
                        <TableCell width="15%">Price</TableCell>
                        <TableCell width="15%">Total</TableCell>
                        {userIsEmployeeOrAdmin(auth) && <TableCell width="2.5%"></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        items.map(item => (<ItemRow key={item.id} item={item} setItems={setItems} suppliers={suppliers} settings={settings} quote={quote}/>))
                    }
                </TableBody>
                {userIsEmployeeOrAdmin(auth) && <TableFooter>
                    <TableRow>
                        <TableCell colSpan={settings.show_supplier_column ? 2 : 1} align="center">
                            <Button fullWidth variant="contained" color="primary" onClick={() => setItems(old => [...old, defaultItem(old.length, quote.quote_no || "", settings)]) }>
                                Add Item
                            </Button>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        {userIsEmployeeOrAdmin(auth) && <TableCell></TableCell>}
                    </TableRow>
                </TableFooter>}
            </Table>
        </Paper>
    )
}

export function roundValue(shouldRound: number, price: number, rounding: number): number {
    return shouldRound === 1 ? Math.ceil(price / rounding) * rounding : price;
}

export function calculateFinancialSummary(quote: Partial<Quote>, items: Array<Item>, setFinancialSummary: SetState<FinancialSummary>, itemDeliveries: Array<ItemDelivery> = [], setItemDeliveryDates: SetState<Array<ItemDeliveryDate>> = () => {}, setSavedItemDeliveryDates: SetState<Array<ItemDeliveryDate>> = () => {}, iteration: number = -1) {
    var result = {
        subtotalExcVAT: 0,
        subtotalVAT: 0,
        serviceExcVAT: 0,
        serviceVAT: 0,
        costTotalExcVAT: 0,
        costVAT: 0,
    }

    for (let item of items) {
        var units = item.units;
        for (let delivered of itemDeliveries) {
            if (delivered.description === item.description && delivered.item_size === item.item_size) {
                units = delivered.delivery_amount;
            }
        }
        // const itemPrice = roundValue(quote.round_values || 0, item.price, 10_000);
        // const itemTotal = itemPrice * units;
        // const itemTotalExcVAT = item.vat === 1 
        //     ? itemTotal / ((10_000 + (quote.vat_rate || 1500)) * 0.0001)
        //     : itemTotal;
        // const itemVAT = itemTotal - itemTotalExcVAT;
        var itemPrice = item.price;
        var itemPriceExcVAT;
        if (item.vat === 1) {
            itemPriceExcVAT = itemPrice / ((10_000 + (quote.vat_rate || 1500)) * 0.0001);
            if (quote.round_values || 0) {
                itemPriceExcVAT = roundValue(1, itemPriceExcVAT, 10_000);
                itemPrice = itemPriceExcVAT * ((10_000 + (quote.vat_rate || 1500)) * 0.0001);
            }
        } else {
            itemPriceExcVAT = itemPrice;
        }
        const itemTotal = itemPrice * units;
        const itemTotalExcVAT = itemPriceExcVAT * units;
        const itemVAT = itemTotal - itemTotalExcVAT;

        result.subtotalExcVAT += itemTotalExcVAT;
        result.subtotalVAT += itemVAT;

        const costTotal = item.cost * units;
        const costTotalExcVAT = item.vat 
            ? costTotal / ((10_000 + (quote.vat_rate || 1500)) * 0.0001)
            : costTotal;
        const costVAT = costTotal - costTotalExcVAT;

        result.costTotalExcVAT += costTotalExcVAT;
        result.costVAT += costVAT;
    }

    const fee_source = quote.service_fee_on_vat_total ? (result.subtotalExcVAT + result.subtotalVAT) : (result.subtotalExcVAT);
    const service_fee = fee_source * ((quote.service_fee || 250) * 0.0001);
    if (quote.service_fee_includes_vat) {
        result.serviceExcVAT = service_fee / ((10_000 + (quote.vat_rate || 1500)) * 0.0001);
        result.serviceVAT = service_fee - result.serviceExcVAT;
    } else {
        result.serviceExcVAT = service_fee;
        result.serviceVAT = service_fee * ((quote.vat_rate || 1500) * 0.0001);
    }
    if (quote.round_values || 0) {
        result.serviceExcVAT = roundValue(1, result.serviceExcVAT, 10_000);
        result.serviceVAT = result.serviceExcVAT * ((10_000 + (quote.vat_rate || 1500)) * 0.0001) - result.serviceExcVAT;
    }

    setFinancialSummary(_summary => ({ 
        ...result, 
        serviceFee: quote.service_fee || 250, 
        vat_rate: quote.vat_rate || 1500,
        roundValues: quote.round_values || 0,
    }));

    if (iteration !== -1) {
        const total_price = Math.trunc(result.serviceExcVAT + result.serviceVAT + result.subtotalExcVAT + result.subtotalVAT);
        const total_cost = Math.trunc(result.costTotalExcVAT + result.costVAT);
        setItemDeliveryDates(dates => dates.map(date => date.iteration === iteration ? { ...date, total_price: total_price, total_cost: total_cost } : date));
        setSavedItemDeliveryDates(dates => dates.map(date => date.iteration === iteration ? { ...date, total_price: total_price, total_cost: total_cost } : date));
    }
}

export function FinancialSummaryTable({ financialSummary, showVatBreakdown }: { financialSummary: FinancialSummary, showVatBreakdown: boolean }) {
    const auth = useContext(UserContext);
    return (
        showVatBreakdown
            ? <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="61%">  </TableCell>
                            <TableCell width="13%"> Total Exc. Vat </TableCell>
                            <TableCell width="13%"> VAT </TableCell>
                            <TableCell width="13%"> Total </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell> Subtotal </TableCell>
                            <TableCell> {formatCurrency(financialSummary.subtotalExcVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.subtotalVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.subtotalExcVAT + financialSummary.subtotalVAT)} </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell> Service Fee @ {financialSummary.roundValues === 1 ? "Approx." : ""} {formatPercentage(financialSummary.serviceFee)}% </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceExcVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceExcVAT + financialSummary.serviceVAT)} </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell> Total </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceExcVAT + financialSummary.subtotalExcVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceVAT + financialSummary.subtotalVAT)} </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceExcVAT + financialSummary.serviceVAT + financialSummary.subtotalVAT + financialSummary.subtotalExcVAT)} </TableCell>
                        </TableRow>
                        {userIsEmployeeOrAdmin(auth) && <TableRow>
                            <TableCell> Profit </TableCell>
                            <TableCell> {formatCurrency(-financialSummary.costTotalExcVAT + financialSummary.serviceExcVAT + financialSummary.subtotalExcVAT)} </TableCell>
                            <TableCell> {formatCurrency(-financialSummary.costVAT + financialSummary.serviceVAT + financialSummary.subtotalVAT)} </TableCell>
                            <TableCell> {formatCurrency(-financialSummary.costTotalExcVAT + -financialSummary.costVAT + financialSummary.serviceExcVAT + financialSummary.serviceVAT + financialSummary.subtotalVAT + financialSummary.subtotalExcVAT)} </TableCell>
                        </TableRow>}
                    </TableBody>
                </Table>
            </Paper>
            : <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="75%">  </TableCell>
                            <TableCell width="25%"> Total </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell> Service Fee @ @ {financialSummary.roundValues === 1 ? "Approx." : ""} {formatPercentage(financialSummary.serviceFee)}% </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceExcVAT)} </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell> Subtotal (Exc. VAT) </TableCell>
                            <TableCell> {formatCurrency(financialSummary.subtotalExcVAT + financialSummary.serviceExcVAT)} </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell> VAT @ ({ formatPercentage(financialSummary.vat_rate) }%)</TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceVAT + financialSummary.subtotalVAT)} </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell> Total </TableCell>
                            <TableCell> {formatCurrency(financialSummary.serviceVAT + financialSummary.subtotalVAT + financialSummary.serviceExcVAT + financialSummary.subtotalExcVAT)} </TableCell>
                        </TableRow>
                        {userIsEmployeeOrAdmin(auth) && <TableRow>
                            <TableCell> Profit </TableCell>
                            <TableCell> {formatCurrency(-financialSummary.costTotalExcVAT + -financialSummary.costVAT + financialSummary.serviceVAT + financialSummary.subtotalVAT + financialSummary.serviceExcVAT + financialSummary.subtotalExcVAT)} </TableCell>
                        </TableRow>}
                    </TableBody>
                </Table>
            </Paper>
    )
}